/**
 * One-way pseudonyms for the identifiers analytics still needs to join events on.
 *
 * The purchase token is a bearer credential: whoever holds it can open the purchase. Email and
 * phone identify a person. None of them may reach Mixpanel, Segment or GTM in clear (U12, R11).
 * Their SHA-256 keeps what analytics uses them for — joining a purchase's events, counting distinct
 * buyers, Google's enhanced conversions — and an analyst who holds a token can still find its
 * events by hashing it the same way.
 *
 * Synchronous on purpose: the trackers run inline with Redux actions, and Web Crypto's digest is
 * async (and absent outside secure contexts).
 */

/* eslint-disable no-bitwise -- SHA-256 is defined in 32-bit word operations */
const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

const rotr = (x, n) => (x >>> n) | (x << (32 - n));

/**
 * The SHA-256 of a string's UTF-8 bytes.
 * @param {string} input - The text to hash.
 * @returns {string} 64 lowercase hex characters.
 */
export const sha256Hex = (input) => {
  const bytes = Array.from(new TextEncoder().encode(String(input)));
  const bitLength = bytes.length * 8;
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  // Messages here are far below 2^32 bits, so the high word of the length is zero.
  bytes.push(0, 0, 0, 0);
  bytes.push(
    (bitLength >>> 24) & 0xff,
    (bitLength >>> 16) & 0xff,
    (bitLength >>> 8) & 0xff,
    bitLength & 0xff,
  );

  const h = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ];
  const w = new Array(64);

  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) {
      const j = offset + i * 4;
      w[i] = (bytes[j] << 24) | (bytes[j + 1] << 16) | (bytes[j + 2] << 8) | bytes[j + 3];
    }
    for (let i = 16; i < 64; i += 1) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }

    let [a, b, c, d, e, f, g, hh] = h;
    for (let i = 0; i < 64; i += 1) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K[i] + w[i]) | 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) | 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }

    h[0] = (h[0] + a) | 0;
    h[1] = (h[1] + b) | 0;
    h[2] = (h[2] + c) | 0;
    h[3] = (h[3] + d) | 0;
    h[4] = (h[4] + e) | 0;
    h[5] = (h[5] + f) | 0;
    h[6] = (h[6] + g) | 0;
    h[7] = (h[7] + hh) | 0;
  }

  return h.map((word) => (word >>> 0).toString(16).padStart(8, '0')).join('');
};
/* eslint-enable no-bitwise */

/**
 * The analytics property that replaces the purchase token, in every tracker. The token is a bearer
 * credential and never leaves for Mixpanel, Segment or GTM; its SHA-256 still joins a purchase's
 * events, and an analyst holding a token finds them by hashing it the same way (U12, R11).
 */
export const PURCHASE_TOKEN_HASH = 'Purchase Token Hash';

/**
 * The pseudonym of a purchase token.
 * @param {string} [token] - The purchase token.
 * @returns {string|undefined} Its SHA-256, or undefined when there is no token.
 */
export const hashPurchaseToken = (token) => (token ? sha256Hex(String(token)) : undefined);

/**
 * The pseudonym of an email, normalised the way Google's enhanced conversions expects: trimmed and
 * lowercased before hashing.
 * @param {string} [email] - The email.
 * @returns {string|undefined} Its SHA-256, or undefined when blank.
 */
export const hashEmail = (email) => {
  const normalised = String(email ?? '')
    .trim()
    .toLowerCase();
  return normalised ? sha256Hex(normalised) : undefined;
};

/**
 * The pseudonym of a phone number, normalised to E.164 (`+` and digits) before hashing.
 * @param {string} [phone] - The phone, country code included when known.
 * @returns {string|undefined} Its SHA-256, or undefined when it holds no digit.
 */
export const hashPhone = (phone) => {
  const digits = String(phone ?? '').replace(/\D/g, '');
  return digits ? sha256Hex(`+${digits}`) : undefined;
};

/**
 * The fields of a buyer or passenger record that identify no one and are reported as they are.
 */
const READABLE_RECORD_FIELDS = new Set([
  'documentType',
  'documentTypeSecond',
  'phoneCountry',
  'phoneCode',
  'nationality',
]);

/**
 * A buyer or passenger record as analytics and error reports may carry it: email and phone hashed,
 * the type fields as they are, and every other field (names, documents, birth date) reduced to
 * `true`, which still answers "was it filled" without carrying the value. A field added to the
 * record later is masked by default.
 * @param {object} [record] - The record.
 * @returns {object} The pseudonymised copy.
 */
export const pseudonymiseRecord = (record = {}) =>
  Object.keys(record || {}).reduce((copy, key) => {
    const value = record[key];
    if (key === 'email') copy.email = hashEmail(value);
    else if (key === 'phone') copy.phone = hashPhone(value);
    else if (READABLE_RECORD_FIELDS.has(key)) copy[key] = value;
    else copy[key] = value !== null && value !== undefined && value !== '';
    return copy;
  }, {});
